import {
  CircularProgressDecoratorColors,
  type CircularProgressDecoratorProps,
} from "@braintrust/braintrust-ui-components";

type ResumeMatchMeta = {
  circularProgressMeta: {
    color: CircularProgressDecoratorProps["color"];
    value: number;
  };
  badgeStyles: {
    color: string;
    backgroundColor: string;
    borderColor: string;
  };
};
const RESUME_MATCH_SCORE_META: Record<
  string,
  {
    value: (jobRoleName: string) => number;
    meta: (score: number) => ResumeMatchMeta;
  }
> = {
  GREAT: {
    value: () => 0.9,
    meta: (score) => {
      return {
        circularProgressMeta: {
          color: CircularProgressDecoratorColors.green,
          value: score * 100,
        },
        badgeStyles: {
          color: "var(--white)",
          backgroundColor: "var(--positive-2)",
          borderColor: "var(--positive-3)",
        },
      };
    },
  },
  GOOD: {
    value: () => 0.8,
    meta: (score) => {
      return {
        circularProgressMeta: {
          color: CircularProgressDecoratorColors.blue,
          value: score * 100,
        },
        badgeStyles: {
          color: "var(--info-1)",
          backgroundColor: "var(--blue)",
          borderColor: "var(--soft-blue)",
        },
      };
    },
  },
  OK: {
    value: (jobRoleName) => {
      return jobRoleName.toLowerCase() === "design" ? 0.5 : 0.6;
    },
    meta: (score: number) => {
      return {
        circularProgressMeta: {
          color: CircularProgressDecoratorColors.yellow,
          value: score * 100,
        },
        badgeStyles: {
          color: "var(--dark-yellow)",
          backgroundColor: "var(--warning-2)",
          borderColor: "var(--soft-orange)",
        },
      };
    },
  },
  BAD: {
    value: () => 0,
    meta: (score) => {
      return {
        circularProgressMeta: {
          color: CircularProgressDecoratorColors.red,
          value: score * 100,
        },
        badgeStyles: {
          color: "var(--negative-1)",
          backgroundColor: "var(--medium-red)",
          borderColor: "var(--soft-red)",
        },
      };
    },
  },
  UNAVAILABLE: {
    value: () => 0,
    meta: (_score) => {
      return {
        circularProgressMeta: {
          color: CircularProgressDecoratorColors.red,
          value: 0,
        },
        badgeStyles: {
          color: "var(--negative-1)",
          backgroundColor: "var(--medium-red)",
          borderColor: "var(--soft-red)",
        },
      };
    },
  },
};

export const getResumeMatchMeta = ({
  matchScore,
  jobRoleName,
  recommendedByMatcher,
}: {
  matchScore: number | undefined;
  jobRoleName: string;
  recommendedByMatcher?: boolean;
}) => {
  let matchMeta: ResumeMatchMeta;
  if (typeof matchScore === "undefined") {
    matchMeta = RESUME_MATCH_SCORE_META.UNAVAILABLE.meta(0);
  } else if (matchScore >= RESUME_MATCH_SCORE_META.GREAT.value(jobRoleName)) {
    matchMeta = RESUME_MATCH_SCORE_META.GREAT.meta(matchScore);
  } else if (matchScore >= RESUME_MATCH_SCORE_META.GOOD.value(jobRoleName)) {
    matchMeta = RESUME_MATCH_SCORE_META.GOOD.meta(matchScore);
  } else if (matchScore >= RESUME_MATCH_SCORE_META.OK.value(jobRoleName)) {
    matchMeta = RESUME_MATCH_SCORE_META.OK.meta(matchScore);
  } else {
    matchMeta = RESUME_MATCH_SCORE_META.BAD.meta(matchScore);
  }

  if (recommendedByMatcher) {
    matchMeta = {
      ...matchMeta,
      circularProgressMeta: {
        value: 100,
        color: CircularProgressDecoratorColors.black,
      },
    };
  }

  return matchMeta;
};
